import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, Chip, Divider, FormControl, FormLabel, InputAdornment, Stack, TextField } from '@mui/material';

const CHIP_COLOR = '#1976d2';

const PRIORITY_OPTIONS = [
    { value: 'Low', label: 'Low', color: '#2e7d32', radioId: 'radioLow' },
    { value: 'Med', label: 'Medium', color: '#ed6c02', radioId: 'radioMed' },
    { value: 'High', label: 'High', color: '#d32f2f', radioId: 'radioHigh' },
];

const FEATURE_BILLABLE_DEFAULT = 56;
const FEATURE_TRAVEL_TIME = 81;
const FEATURE_EXPENSES = 82;

const getFeatureSetting = (user, featureId) =>
    user?.comp_settings?.find((s) => s.feature_id === featureId);

const selectableChipSx = (selected, color = CHIP_COLOR) => ({
    flex: 1,
    justifyContent: 'center',
    bgcolor: selected ? color : 'transparent',
    color: selected ? '#fff' : color,
    border: `1px solid ${color}`,
    fontWeight: 500,
    cursor: 'pointer',
    '&:hover': { bgcolor: selected ? color : `${color}1a` },
});

const YesNoChips = ({ yesSelected, onYes, onNo }) => (
    <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
        <Chip label="Yes" onClick={onYes} sx={selectableChipSx(yesSelected)} />
        <Chip label="No" onClick={onNo} sx={selectableChipSx(!yesSelected)} />
    </Box>
);

const FinalDetsCard = (props) => {
    const [billableByDefault, setBillableByDefault] = useState(true);
    const [taskTravelTimeEnabled, setTaskTravelTimeEnabled] = useState(false);
    const [taskExpensesEnabled, setTaskExpensesEnabled] = useState(false);

    useEffect(() => {
        const billableSetting = getFeatureSetting(props.user, FEATURE_BILLABLE_DEFAULT);
        setBillableByDefault(!billableSetting || !!billableSetting.feature_value);
        setTaskTravelTimeEnabled(getFeatureSetting(props.user, FEATURE_TRAVEL_TIME)?.feature_value === 'true');
        setTaskExpensesEnabled(getFeatureSetting(props.user, FEATURE_EXPENSES)?.feature_value === 'true');
    }, [])

    const empList = props.edit
        ? props.empList.map((emp) => ({ value: emp.emp_id, title: `${emp.emp_first_name} ${emp.emp_last_name}` }))
        : props.empList.filter(Boolean).map((emp) => ({ value: emp.id, title: emp.employee_name }));

    return (
        <Stack spacing={2}>
            <Autocomplete
                id="tAssTo"
                fullWidth
                value={empList.find((emp) => emp.value === props.formVals.tAssTo) || null}
                size="small"
                onChange={(e, newValue) => {
                    let value = empList.find((emp) => emp.title === newValue.title)
                    if (!!value) {
                        props.handleProjLoc(value.value, 'tAssTo')
                        props.handleProjLoc(props.empList.filter(emp => !!emp).find(emp => emp.id === newValue.value)?.emp_timezone || '', 'task_emp_timezone')
                    }
                    return
                }}
                options={empList}
                getOptionLabel={(option) => option.title}
                renderInput={(params) => {
                    const errAssTo = props.submitAttempted && !props.formVals.tAssTo;
                    return <TextField {...params} label="Assigned To" required variant="outlined" error={errAssTo} helperText={errAssTo ? 'Assigned To is required' : ''} />;
                }}
            />

            <FormControl component="fieldset" required sx={{ width: '100%' }}>
                <FormLabel component="legend" required sx={{ fontSize: '0.85rem' }}>
                    Priority
                </FormLabel>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                    {PRIORITY_OPTIONS.map((opt) => {
                        const selected = props.formVals.tPriority === opt.value;
                        return (
                            <Chip
                                key={opt.value}
                                label={opt.label}
                                onClick={() => props.handleFinalDetsChange({ target: { id: opt.radioId, name: 'tPriority', value: opt.value } })}
                                sx={selectableChipSx(selected, opt.color)}
                            />
                        );
                    })}
                </Box>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1.5 }}>
                <FormControl component="fieldset" required sx={{ flex: 1, minWidth: 0 }}>
                    <FormLabel component="legend" required sx={{ fontSize: '0.85rem' }}>
                        Billable to Customer
                    </FormLabel>
                    <YesNoChips
                        yesSelected={billableByDefault === true}
                        onYes={() => {
                            props.handleFinalDetsChange({ target: { id: 'radioBillYes', name: 'tIsBillable' } });
                            setBillableByDefault(true);
                        }}
                        onNo={() => {
                            props.handleFinalDetsChange({ target: { id: 'radioBillNo', name: 'tIsBillable' } });
                            setBillableByDefault(false);
                        }}
                    />
                </FormControl>
                <Divider orientation="vertical" flexItem />
                <FormControl component="fieldset" required sx={{ flex: 1, minWidth: 0 }}>
                    <FormLabel component="legend" required sx={{ fontSize: '0.85rem' }}>
                        Signature Required
                    </FormLabel>
                    <YesNoChips
                        yesSelected={props.formVals.tSigReq === 1}
                        onYes={() => props.handleFinalDetsChange({ target: { id: 'radioSigYes', name: 'tSigReq' } })}
                        onNo={() => props.handleFinalDetsChange({ target: { id: 'radioSigNo', name: 'tSigReq' } })}
                    />
                </FormControl>
            </Box>

            <Autocomplete
                id="tContacts"
                fullWidth
                size="small"
                multiple
                value={props?.formVals?.tContacts}
                options={props?.contactList || []}
                renderInput={(params) => <TextField {...params} label="Contacts (Optional)" variant="outlined" />}
                onChange={(event, value) => props.handleFinalDetsChange({ target: { id: 'tContacts', value: value } })}
            />

            <TextField
                id="tNotes"
                label="Notes (Optional)"
                variant="outlined"
                size="small"
                fullWidth
                value={props.formVals.tComment}
                onChange={(event) => props.handleFinalDetsChange(event)}
                multiline
                minRows={3}
                inputProps={{ maxLength: 4000 }}
            />

            {(taskTravelTimeEnabled || taskExpensesEnabled) && (
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    {taskTravelTimeEnabled && (
                        <TextField
                            id="tTravelTime"
                            label="Travel Time (Optional)"
                            type="text"
                            variant="outlined"
                            size="small"
                            sx={{ flex: 1, minWidth: 0 }}
                            value={props.formVals.tTravelTime || ''}
                            onChange={(event) => props.handleFinalDetsChange(event)}
                            placeholder="HH:MM (e.g., 02:30)"
                            helperText="Enter duration in HH:MM format"
                            FormHelperTextProps={{ sx: { color: '#ed6c02' } }}
                            inputProps={{ pattern: "[0-9]{1,3}:[0-5][0-9]" }}
                        />
                    )}
                    {taskExpensesEnabled && (
                        <TextField
                            id="tExpenses"
                            label="Expenses (Optional)"
                            type="number"
                            variant="outlined"
                            size="small"
                            sx={{ flex: 1, minWidth: 0 }}
                            value={props.formVals.tExpenses || ''}
                            onChange={(event) => props.handleFinalDetsChange(event)}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            inputProps={{ min: 0, step: 0.01 }}
                            placeholder="0.00"
                        />
                    )}
                </Box>
            )}

        </Stack>
    )
};

export default FinalDetsCard;
